import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  type PlacetelOpeningDayInput,
  PlacetelOpeningHoursDocument,
  type PlacetelOpeningHoursQuery,
  UpdatePlacetelOpeningHoursDocument,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";

type DayDraft = { open: boolean; opens: string; closes: string };

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
const DEFAULT_OPENS = "08:00";
const DEFAULT_CLOSES = "18:00";
const OPENING_HOURS_INVALID_CODE = "placetel-opening-hours-invalid";

// MUI types the input attributes of a Switch without data-* attributes.
const testIdProps = (testId: string): Record<string, string> => ({ "data-testid": testId });

type FieldError = "blank" | "beforeCloses" | null;

// A closed day has no problem; an open day needs both times, opening before closing.
const opensError = (day: DayDraft): FieldError => {
  if (!day.open) return null;
  if (day.opens === "") return "blank";
  return day.closes !== "" && day.opens >= day.closes ? "beforeCloses" : null;
};

const closesError = (day: DayDraft): FieldError => (day.open && day.closes === "" ? "blank" : null);

// The drafts of Monday to Sunday; a day the server does not list is closed.
const toDrafts = (days: PlacetelOpeningHoursQuery["placetelOpeningHours"]): Record<number, DayDraft> =>
  Object.fromEntries(
    WEEKDAYS.map(weekday => {
      const listed = days.find(day => day.weekday === weekday);
      return [
        weekday,
        listed
          ? { open: true, opens: listed.opens, closes: listed.closes }
          : { open: false, opens: DEFAULT_OPENS, closes: DEFAULT_CLOSES },
      ];
    })
  );

const toInput = (drafts: Record<number, DayDraft>): PlacetelOpeningDayInput[] =>
  WEEKDAYS.filter(weekday => drafts[weekday].open).map(weekday => ({
    weekday,
    opens: drafts[weekday].opens,
    closes: drafts[weekday].closes,
  }));

export const OpeningHoursSettings = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data, loading, error } = useQuery(PlacetelOpeningHoursDocument, { fetchPolicy: "network-only" });
  const [drafts, setDrafts] = useState<Record<number, DayDraft>>(() => toDrafts([]));
  const [initialized, setInitialized] = useState(false);
  const saveInFlight = useRef(false);
  const [updatePlacetelOpeningHours, { loading: saving }] = useMutation(UpdatePlacetelOpeningHoursDocument, {
    update: (cache, { data: mutationData }) => {
      const saved = mutationData?.updatePlacetelOpeningHours;
      if (!saved) return;
      cache.writeQuery({ query: PlacetelOpeningHoursDocument, data: { placetelOpeningHours: saved } });
    },
  });

  const days = data?.placetelOpeningHours;

  useEffect(() => {
    if (loading || error || !days || initialized) return;
    setDrafts(toDrafts(days));
    setInitialized(true);
  }, [days, error, initialized, loading]);

  const changeDay = (weekday: number, change: Partial<DayDraft>) =>
    setDrafts(current => ({ ...current, [weekday]: { ...current[weekday], ...change } }));

  const hasProblem = WEEKDAYS.some(weekday => opensError(drafts[weekday]) || closesError(drafts[weekday]));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!initialized || saving || saveInFlight.current || hasProblem) return;

    saveInFlight.current = true;
    try {
      const result = await updatePlacetelOpeningHours({ variables: { input: toInput(drafts) } });
      const saved = result.data?.updatePlacetelOpeningHours;
      if (!saved) throw new Error();

      setDrafts(toDrafts(saved));
      notify("integrations.openingHours.saved", { type: "success" });
    } catch (saveError) {
      const refused =
        saveError instanceof ApolloError && saveError.graphQLErrors[0]?.message === OPENING_HOURS_INVALID_CODE;
      if (!refused) captureException(saveError, "placetel/opening-hours: save");
      notify(translate("integrations.openingHours.saveError"), { type: "error" });
    } finally {
      saveInFlight.current = false;
    }
  };

  if (error) return <Alert severity="error">{translate("integrations.openingHours.loadError")}</Alert>;
  if (loading || !days || !initialized) {
    return <CircularProgress aria-label={translate("integrations.openingHours.loading")} />;
  }

  const errorMessage = (problem: FieldError): string | undefined =>
    problem ? translate(`integrations.openingHours.${problem}`) : undefined;

  return (
    <Card data-testid="opening-hours-card">
      <CardContent>
        <Typography component="h2" variant="h6" gutterBottom>
          {translate("integrations.openingHours.title")}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {translate("integrations.openingHours.description")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {WEEKDAYS.map(weekday => {
            const day = drafts[weekday];
            const opensProblem = errorMessage(opensError(day));
            const closesProblem = errorMessage(closesError(day));
            return (
              <Box key={weekday} sx={{ alignItems: "flex-start", display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
                <Typography component="h3" sx={{ minWidth: 100, pt: 2 }} variant="subtitle1">
                  {translate(`integrations.openingHours.days.${weekday}`)}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={day.open}
                      disabled={saving}
                      onChange={event => changeDay(weekday, { open: event.target.checked })}
                      inputProps={testIdProps(`opening-day-${weekday}-open`)}
                    />
                  }
                  label={translate("integrations.openingHours.open")}
                  sx={{ pt: 1 }}
                />
                <TextField
                  disabled={saving || !day.open}
                  error={Boolean(opensProblem)}
                  helperText={opensProblem}
                  label={translate("integrations.openingHours.opens")}
                  margin="dense"
                  onChange={event => changeDay(weekday, { opens: event.target.value })}
                  size="small"
                  slotProps={{
                    htmlInput: { "data-testid": `opening-day-${weekday}-opens` },
                    inputLabel: { shrink: true },
                  }}
                  type="time"
                  value={day.opens}
                />
                <TextField
                  disabled={saving || !day.open}
                  error={Boolean(closesProblem)}
                  helperText={closesProblem}
                  label={translate("integrations.openingHours.closes")}
                  margin="dense"
                  onChange={event => changeDay(weekday, { closes: event.target.value })}
                  size="small"
                  slotProps={{
                    htmlInput: { "data-testid": `opening-day-${weekday}-closes` },
                    inputLabel: { shrink: true },
                  }}
                  type="time"
                  value={day.closes}
                />
              </Box>
            );
          })}
          <Button
            data-testid="opening-hours-save"
            disabled={saving || hasProblem}
            sx={{ mt: 2 }}
            type="submit"
            variant="contained"
          >
            {translate(saving ? "integrations.openingHours.saving" : "integrations.openingHours.save")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
